/* eslint-disable no-continue */
import type {
  FetchResult,
  ObservableSubscription,
  Operation,
} from '@apollo/client';
import { ApolloLink, HttpLink, Observable } from '@apollo/client';
import type { HttpOptions } from '@apollo/client/link/http';
import { createKeyspace, skey } from '@frameio/basics/alg/keys';
import { StructMap } from '@frameio/basics/ds/struct-map';
import { CONFIG } from '@frameio/config';
import { hasSubscription } from '../../utils/hasSubscription';
import createAbsintheSocketLink from './createAbsintheSocketLink';
import {
  assignedEndpoints,
  extractEntityIds,
  recordRedirect,
} from './regionForEntity';

export type RegionalLinkOptions = Omit<HttpOptions, 'uri'> & {
  maxRedirects?: number;
  /**
   * Run subscriptions over per-region sockets. When false (e.g. on the server
   * or in workers), subscriptions go over HTTP like every other operation.
   */
  sockets?: boolean;
};

/**
 * Per-link endpoint state. `last` is where a new operation goes when none of its
 * entities have been redirected; `known` is every endpoint this link has reason
 * to believe exists, and is where unassigned subscriptions fan out to.
 */
export class EndpointTracker {
  last: string = CONFIG.graphqlUrl;

  readonly known = new Set<string>([CONFIG.graphqlUrl]);

  /** Records a successful response from `endpoint`; true if it's new. */
  succeeded(endpoint: string): boolean {
    this.last = endpoint;
    if (this.known.has(endpoint)) return false;
    this.known.add(endpoint);
    return true;
  }

  /**
   * The endpoint owning the most of these variables' entities, else `last`.
   * Ties go to `last` when it's among them, since it's known to answer.
   */
  endpointFor(variables: unknown): string {
    let best = this.last;
    let bestCount = 0;
    for (const [endpoint, count] of assignedEndpoints(
      extractEntityIds(variables)
    )) {
      if (
        count > bestCount ||
        (count === bestCount && endpoint === this.last)
      ) {
        best = endpoint;
        bestCount = count;
      }
    }
    return best;
  }
}

function findRegionRedirect(result: FetchResult): string | undefined {
  const redirectUrl = result.errors?.find(
    (error) => error.extensions?.code === 'REGION_REDIRECT'
  )?.extensions?.redirectUrl;
  return typeof redirectUrl === 'string' ? redirectUrl : undefined;
}

type Observer = {
  next: (value: FetchResult) => void;
  error: (error: unknown) => void;
  complete: () => void;
};

type ChildSubscription = { unsubscribe: () => void };

type SubscriptionEntry = {
  observers: Set<Observer>;
  subs: Map<string, ChildSubscription>;
};

/**
 * Map a regional HTTP endpoint onto its socket URL by carrying that region's
 * hostname and port over onto the configured socket URL. The path
 * (`/socket-gql`) stays put; the scheme follows the endpoint's (http -> ws).
 */
export function socketUrlForEndpoint(endpoint: string): string {
  try {
    const target = new URL(endpoint);
    const socketUrl = new URL(CONFIG.socketUrl);
    socketUrl.protocol = target.protocol === 'http:' ? 'ws:' : 'wss:';
    socketUrl.hostname = target.hostname;
    // Set explicitly: assigning `host` without a port keeps the old one.
    socketUrl.port = target.port;
    return socketUrl.toString();
  } catch {
    return CONFIG.socketUrl;
  }
}

/**
 * A link that routes every operation by region.
 *
 * Queries and mutations go over HTTP to the endpoint owning most of their
 * entities (else the last endpoint that answered), and are re-issued wherever a
 * `REGION_REDIRECT` error points.
 *
 * Subscriptions go over one socket per endpoint: to the endpoints owning their
 * entities, or to every endpoint this link knows of if none are assigned yet,
 * so no events are missed. Their state is a single `Operation -> { observers,
 * subs }` map that `reconcile` drives towards each operation's targets. It
 * re-runs whenever this link learns of a redirect or a new endpoint, since
 * either can change where existing subscriptions belong.
 */
export function createRegionalLink(
  options: RegionalLinkOptions = {}
): ApolloLink {
  const { maxRedirects = 2, sockets = true, ...httpOptions } = options;
  const http = new HttpLink(httpOptions);
  const endpoints = new EndpointTracker();
  const socketLinks = new Map<string, ApolloLink>();
  const subscriptions = new StructMap<Operation, SubscriptionEntry>(opKey);

  function redirected(operation: Operation, redirectTo: string) {
    recordRedirect(extractEntityIds(operation.variables), redirectTo);
    reconcile();
  }

  function succeeded(endpoint: string) {
    if (endpoints.succeeded(endpoint)) reconcile();
  }

  function overHttp(operation: Operation): Observable<FetchResult> {
    return new Observable<FetchResult>((observer) => {
      let redirects = 0;
      let current: ObservableSubscription | undefined;

      const attempt = (endpoint: string) => {
        let superseded = false;
        operation.setContext({ uri: endpoint });
        current = http.request(operation)?.subscribe({
          next(result) {
            if (superseded) return;
            const redirectTo = findRegionRedirect(result);
            if (!redirectTo) {
              succeeded(endpoint);
              observer.next(result);
              return;
            }
            superseded = true;
            current?.unsubscribe();
            redirected(operation, redirectTo);
            if (redirects === maxRedirects) {
              observer.error(
                new Error(`Redirected more than ${maxRedirects} times`)
              );
              return;
            }
            redirects += 1;
            attempt(redirectTo);
          },
          error(error) {
            if (!superseded) observer.error(error);
          },
          complete() {
            if (!superseded) observer.complete();
          },
        });
      };

      attempt(endpoints.endpointFor(operation.variables));
      return () => current?.unsubscribe();
    });
  }

  function socketLinkFor(endpoint: string): ApolloLink {
    let link = socketLinks.get(endpoint);
    if (!link) {
      link = createAbsintheSocketLink(socketUrlForEndpoint(endpoint));
      socketLinks.set(endpoint, link);
    }
    return link;
  }

  function targetsFor(operation: Operation): Set<string> {
    const assigned = assignedEndpoints(extractEntityIds(operation.variables));
    return assigned.size ? new Set(assigned.keys()) : endpoints.known;
  }

  const fanOut = (operation: Operation, emit: (observer: Observer) => void) => {
    subscriptions.get(operation)?.observers.forEach(emit);
  };

  function handleSocketResult(
    operation: Operation,
    endpoint: string,
    value: FetchResult
  ) {
    const redirectTo = findRegionRedirect(value);
    if (redirectTo) {
      redirected(operation, redirectTo);
      return;
    }
    succeeded(endpoint);
    fanOut(operation, (observer) => observer.next(value));
  }

  function reconcile() {
    for (const [operation, entry] of subscriptions) {
      if (entry.observers.size === 0) {
        entry.subs.forEach((sub) => sub.unsubscribe());
        subscriptions.delete(operation);
        continue;
      }

      const targets = targetsFor(operation);

      for (const [endpoint, sub] of entry.subs) {
        if (!targets.has(endpoint)) {
          sub.unsubscribe();
          entry.subs.delete(endpoint);
        }
      }

      for (const endpoint of targets) {
        if (entry.subs.has(endpoint)) {
          continue;
        }
        const result = socketLinkFor(endpoint).request(operation);
        if (!result) {
          continue;
        }
        const sub = result.subscribe({
          next: (value) => handleSocketResult(operation, endpoint, value),
          error: (error) =>
            fanOut(operation, (observer) => observer.error(error)),
        });
        entry.subs.set(endpoint, sub);
      }
    }
  }

  function overSocket(operation: Operation): Observable<FetchResult> {
    return new Observable<FetchResult>((observer) => {
      let entry = subscriptions.get(operation);
      if (!entry) {
        entry = { observers: new Set(), subs: new Map() };
        subscriptions.set(operation, entry);
      }
      entry.observers.add(observer);
      reconcile();

      return () => {
        entry.observers.delete(observer);
        reconcile();
      };
    });
  }

  return new ApolloLink((operation) =>
    sockets && hasSubscription(operation.query)
      ? overSocket(operation)
      : overHttp(operation)
  );
}

const opKey = createKeyspace(
  (ref) => (op: Operation) =>
    skey({
      ...op,
      query: ref(op.query),
      context: op.getContext(),
    })
);
